import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Alert, CircularProgress, Breadcrumbs, Link, InputAdornment, IconButton,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Chip from '@mui/material/Chip';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

const ROLES = [
    { value: 'admin', label: 'Admin', color: '#e85a2c' },
    { value: 'gruppi', label: 'Gruppi', color: '#2196f3' }
];

export const UsersCreatePage = () => {
    const navigate = useNavigate();
    const notify = useNotify();

    const [formData, setFormData] = React.useState({
        email: '',
        password: '',
        confirmPassword: '',
        nome: '',
        cognome: '',
        roles: 'gruppi',
        gruppiIds: [], // Array per selezione multipla
        status: true
    });

    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
    const [gruppi, setGruppi] = React.useState([]);
    const [loadingGruppi, setLoadingGruppi] = React.useState(false);

    const loadGruppi = async () => {
        try {
            setLoadingGruppi(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-gruppo`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            // Escludi gruppo Admin (id=3) e Guest (id=5)
            const gruppiFiltered = (response.data.data || []).filter(g => g.id !== 3 && g.id !== 5);
            setGruppi(gruppiFiltered);
        } catch (error) {
            console.error("Errore caricamento gruppi:", error);
            notify("Errore nel caricamento gruppi", { type: 'error' });
        } finally {
            setLoadingGruppi(false);
        }
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'email':
                if (!value.trim()) {
                    newErrors.email = 'L\'email è obbligatoria';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    newErrors.email = 'Email non valida';
                } else {
                    delete newErrors.email;
                }
                break;

            case 'password':
                if (!value) {
                    newErrors.password = 'La password è obbligatoria';
                } else if (value.length < 6) {
                    newErrors.password = 'La password deve essere di almeno 6 caratteri';
                } else {
                    delete newErrors.password;
                }
                // Rivalidare confirmPassword se già inserita
                if (formData.confirmPassword && value !== formData.confirmPassword) {
                    newErrors.confirmPassword = 'Le password non coincidono';
                } else if (formData.confirmPassword) {
                    delete newErrors.confirmPassword;
                }
                break;

            case 'confirmPassword':
                if (!value) {
                    newErrors.confirmPassword = 'Conferma la password';
                } else if (value !== formData.password) {
                    newErrors.confirmPassword = 'Le password non coincidono';
                } else {
                    delete newErrors.confirmPassword;
                }
                break;

            case 'nome':
                if (!value.trim()) {
                    newErrors.nome = 'Il nome è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.nome = 'Il nome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.nome;
                }
                break;

            case 'cognome':
                if (!value.trim()) {
                    newErrors.cognome = 'Il cognome è obbligatorio';
                } else if (value.trim().length < 2) {
                    newErrors.cognome = 'Il cognome deve essere di almeno 2 caratteri';
                } else {
                    delete newErrors.cognome;
                }
                break;

            case 'gruppiIds':
                if (formData.roles === 'gruppi' && (!value || value.length === 0)) {
                    newErrors.gruppiIds = 'Seleziona almeno un gruppo';
                } else {
                    delete newErrors.gruppiIds;
                }
                break;

            default:
                delete newErrors[name];
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));

        // Se cambia il ruolo, resetta gruppiIds
        if (name === 'roles') {
            setFormData(prev => ({ ...prev, gruppiIds: [] }));
            setTouched(prev => ({ ...prev, gruppiIds: false }));
        }

        validateField(name, value);
    };

    const validateForm = () => {
        const fieldsToValidate = ['email', 'password', 'confirmPassword', 'nome', 'cognome'];

        // Aggiungi validazione gruppiIds solo se ruolo è 'gruppi'
        if (formData.roles === 'gruppi') {
            fieldsToValidate.push('gruppiIds');
        }

        let isValid = true;

        fieldsToValidate.forEach(field => {
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
            setTouched(prev => ({ ...prev, [field]: true }));
        });

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            notify("Correggere gli errori nel form", { type: 'error' });
            return;
        }

        try {
            setSaving(true);
            const jwt = getJwt();

            const dataToSend = {
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                nome: formData.nome.trim(),
                cognome: formData.cognome.trim(),
                roles: formData.roles,
                status: formData.status ? 1 : 0
            };

            // Crea associazione gruppo dopo creazione utente
            const userResponse = await axios.post(`${host}/users`, dataToSend, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            const userId = userResponse.data.id;

            // Crea associazioni tbl_users_gruppo
            const gruppiToAssign = formData.roles === 'admin' ? [3] : formData.gruppiIds;

            for (const gruppoId of gruppiToAssign) {
                await axios.post(`${host}/tbl-users-gruppo`, {
                    usersId: userId,
                    tbl_gruppoId: parseInt(gruppoId),
                    flVisibile: 1,
                    flAttivo: 1,
                    flAnnullato: 0,
                    ordinamento: 0
                }, {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                });
            }

            notify(`Utente "${formData.nome} ${formData.cognome}" creato con successo`, { type: 'success' });
            navigate('/users');

        } catch (error) {
            console.error("Errore creazione utente:", error);
            if (error.response?.data?.errors?.some(e => e.message === 'email must be unique')) {
                setErrorDialogOpen(true);
            } else if (error.response?.status === 409 || error.response?.data?.message?.includes('email')) {
                notify("Email già esistente", { type: 'error' });
            } else {
                notify(error.response?.data?.message || "Errore nella creazione dell'utente", { type: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setFormData({
            email: '', password: '', confirmPassword: '', nome: '', cognome: '', roles: 'gruppi', gruppiIds: [], status: true
        });
        setErrors({});
        setTouched({});
    };

    React.useEffect(() => {
        loadGruppi();
    }, []);

    const hasErrors = Object.keys(errors).length > 0;
    const canSave = formData.email.trim() && formData.password && formData.confirmPassword &&
        formData.nome.trim() && formData.cognome.trim() && !hasErrors &&
        (formData.roles === 'admin' || (formData.roles === 'gruppi' && formData.gruppiIds.length > 0));

    const selectedRole = ROLES.find(r => r.value === formData.roles);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>



                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 32, verticalAlign: 'middle', mr: 1 }} />
                            Crea Nuovo Utente
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Inserisci i dettagli per creare un nuovo utente
                        </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
                        Indietro
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Informazioni Utente</Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nome *"
                                            value={formData.nome}
                                            onChange={(e) => handleFieldChange('nome', e.target.value)}
                                            error={touched.nome && !!errors.nome}
                                            helperText={touched.nome && errors.nome}
                                            placeholder="Inserisci il nome"
                                            autoComplete="off"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Cognome *"
                                            value={formData.cognome}
                                            onChange={(e) => handleFieldChange('cognome', e.target.value)}
                                            error={touched.cognome && !!errors.cognome}
                                            helperText={touched.cognome && errors.cognome}
                                            placeholder="Inserisci il cognome"
                                            autoComplete="off"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Email *"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleFieldChange('email', e.target.value)}
                                            error={touched.email && !!errors.email}
                                            helperText={touched.email && errors.email}
                                            placeholder="utente@esempio.com"
                                            autoComplete="off"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Password *"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => handleFieldChange('password', e.target.value)}
                                            error={touched.password && !!errors.password}
                                            helperText={touched.password && errors.password || "Minimo 6 caratteri"}
                                            autoComplete="new-password"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Conferma Password *"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                                            error={touched.confirmPassword && !!errors.confirmPassword}
                                            helperText={touched.confirmPassword && errors.confirmPassword}
                                            autoComplete="new-password"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Ruolo</InputLabel>
                                            <Select
                                                value={formData.roles}
                                                label="Ruolo"
                                                onChange={(e) => handleFieldChange('roles', e.target.value)}
                                            >
                                                {ROLES.map(role => (
                                                    <MenuItem key={role.value} value={role.value}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: role.color }} />
                                                            {role.label}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {formData.roles === 'gruppi' && (
                                        <Grid item xs={12}>
                                            <FormControl fullWidth error={touched.gruppiIds && !!errors.gruppiIds}>
                                                <InputLabel>Gruppi *</InputLabel>
                                                <Select
                                                    multiple
                                                    value={formData.gruppiIds}
                                                    label="Gruppi *"
                                                    onChange={(e) => handleFieldChange('gruppiIds', e.target.value)}
                                                    disabled={loadingGruppi}
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const gruppo = gruppi.find(g => g.id === value);
                                                                return (
                                                                    <Chip key={value} label={gruppo?.nome || value} size="small" />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                >
                                                    {loadingGruppi ? (
                                                        <MenuItem disabled>
                                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                                            Caricamento...
                                                        </MenuItem>
                                                    ) : (
                                                        gruppi.map(gruppo => (
                                                            <MenuItem key={gruppo.id} value={gruppo.id}>
                                                                {gruppo.nome}
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </Select>
                                                {touched.gruppiIds && errors.gruppiIds && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                                        {errors.gruppiIds}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {formData.roles === 'admin' && (
                                        <Grid item xs={12}>
                                            <Alert severity="info">
                                                L'utente Admin verrà automaticamente assegnato al gruppo "Admin" (ID: 3)
                                            </Alert>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Anteprima Utente</Typography>
                                <PersonIcon sx={{ fontSize: 60, color: selectedRole?.color || '#2196f3', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {formData.nome || formData.cognome ? `${formData.nome} ${formData.cognome}` : 'Nome Cognome'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {formData.email || 'email@esempio.com'}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: selectedRole?.color }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {selectedRole?.label || 'Gruppi'}
                                    </Typography>
                                </Box>
                                {formData.roles === 'gruppi' && formData.gruppiIds.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Gruppi selezionati:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                            {formData.gruppiIds.map(id => {
                                                const gruppo = gruppi.find(g => g.id === id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={gruppo?.nome || id}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}
                                {formData.roles === 'admin' && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Gruppo: Admin (ID: 3)
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Impostazioni</Typography>
                                <FormControlLabel
                                    control={<Switch checked={formData.status} onChange={(e) => handleFieldChange('status', e.target.checked)} color="primary" />}
                                    label="Utente attivo"
                                />
                            </CardContent>
                        </Card>

                        {hasErrors && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Correggere i seguenti errori:</Typography>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    {Object.values(errors).map((error, index) => (
                                        <li key={index}><Typography variant="body2">{error}</Typography></li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={!canSave || saving}
                                sx={{ py: 1.5 }}
                            >
                                {saving ? 'Salvataggio...' : 'Crea Utente'}
                            </Button>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} disabled={saving}>
                                Reset Form
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">* Campi obbligatori</Typography>
                </Box>

            </motion.div>

            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
            >
                <DialogTitle>Errore Creazione Utente</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        L'email è già adoperata su altro account
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} color="primary" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
